import React, { useMemo, useState } from "react";
import {
  Box, Button, CircularProgress, Grid, IconButton, LinearProgress, Stack, TextField, Typography, Chip,
} from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api/client";
import { PageHeader, SectionCard } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";

export default function TimesheetFormNew() {
  const nav = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [tasks, setTasks] = useState([{ taskName: "", duration: "", projectName: "" }]);
  const [loading, setLoading] = useState(false);

  const totalHours = useMemo(
    () => tasks.reduce((s, t) => s + (parseFloat(t.duration) || 0), 0),
    [tasks]
  );

  const update = (i, field, value) => {
    if (field === "duration" && value && !/^\d*\.?\d*$/.test(value)) return;
    setTasks((ts) => ts.map((t, idx) => (idx === i ? { ...t, [field]: value } : t)));
  };
  const addTask = () => setTasks((ts) => [...ts, { taskName: "", duration: "", projectName: "" }]);
  const removeTask = (i) => setTasks((ts) => ts.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Date is required");
    if (tasks.some((t) => !t.taskName.trim() || !t.duration)) return toast.error("Fill all task details");
    if (totalHours <= 0) return toast.error("Total hours must be > 0");
    if (totalHours > 24) return toast.error("Total cannot exceed 24 hours");
    setLoading(true);
    try {
      // Coerce duration to Number — backend validator rejects string durations,
      // and the controller also expects a `hoursWorked` total on the top-level doc.
      const normalizedTasks = tasks.map((t) => ({
        ...t,
        duration: Number(t.duration) || 0,
      }));
      await api.post("/api/timesheet/", {
        date,
        hoursWorked: totalHours,
        taskDetails: normalizedTasks,
      });
      toast.success("Timesheet saved");
      nav("/viewAllTimesheets");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setLoading(false); }
  };

  const pct = Math.min((totalHours / 8) * 100, 100);
  const overUnder = totalHours < 8 ? "under" : totalHours > 8 ? "over" : "on";

  return (
    <Box p={3}>
      <PageHeader
        title="Log Timesheet"
        subtitle="Record your daily work hours per task"
        icon={<AccessTimeOutlinedIcon />}
      />

      <Box component="form" onSubmit={submit}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <SectionCard title="Date" accent="#6C50BF" sx={{ mb: 3 }}>
              <TextField
                fullWidth type="date" label="Work Date" InputLabelProps={{ shrink: true }}
                value={date} onChange={(e) => setDate(e.target.value)}
                inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                required
              />
            </SectionCard>

            <SectionCard
              title="Tasks"
              subtitle={`${tasks.length} ${tasks.length === 1 ? "task" : "tasks"}`}
              accent="#39C645"
              actions={[
                <Button key="add" variant="outlined" size="small" startIcon={<AddIcon />} onClick={addTask}>
                  Add Task
                </Button>,
              ]}
            >
              <Stack spacing={2}>
                {tasks.map((t, i) => (
                  <Box key={i} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 3, position: "relative" }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Chip label={`Task ${i + 1}`} size="small" sx={{ bgcolor: "primary.main", color: "#fff", fontWeight: 700 }} />
                      {tasks.length > 1 && (
                        <IconButton size="small" color="error" onClick={() => removeTask(i)}>
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={6}><TextField fullWidth size="small" label="Task name *" value={t.taskName} onChange={(e) => update(i, "taskName", e.target.value)} required /></Grid>
                      <Grid item xs={8} md={4}><TextField fullWidth size="small" label="Project (optional)" value={t.projectName} onChange={(e) => update(i, "projectName", e.target.value)} /></Grid>
                      <Grid item xs={4} md={2}><TextField fullWidth size="small" label="Hours *" placeholder="e.g. 2.5" value={t.duration} onChange={(e) => update(i, "duration", e.target.value)} required /></Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Box sx={{ position: { lg: "sticky" }, top: { lg: 88 } }}>
              <SectionCard title="Summary" accent="#F9BC06">
                <Stack spacing={2}>
                  <Box textAlign="center" sx={{ p: 2.5, borderRadius: 3, bgcolor: "primary.lighter" }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Total Logged
                    </Typography>
                    <Typography sx={{ fontSize: 44, fontWeight: 800, color: "primary.main", lineHeight: 1 }}>
                      {totalHours.toFixed(1)}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>hours of 8.0</Typography>
                    <LinearProgress
                      variant="determinate" value={pct}
                      sx={{ mt: 1.5, height: 8, borderRadius: 4,
                        "& .MuiLinearProgress-bar": { bgcolor: overUnder === "over" ? "#F9BC06" : overUnder === "on" ? "#39C645" : "#6C50BF" },
                      }}
                    />
                    <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 1 }}>
                      {overUnder === "over" ? `${(totalHours - 8).toFixed(1)} hrs over standard day` :
                       overUnder === "on" ? "Standard 8-hour day" :
                       `${(8 - totalHours).toFixed(1)} hrs remaining`}
                    </Typography>
                  </Box>

                  <Button
                    type="submit" variant="contained" size="large" fullWidth
                    disabled={loading || totalHours === 0}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />}
                    sx={{ py: 1.25 }}
                  >
                    {loading ? "Saving…" : "Submit Timesheet"}
                  </Button>

                  <Typography sx={{ fontSize: 11, color: "text.secondary", textAlign: "center" }}>
                    Entries can be edited from the timesheet list until locked by your manager.
                  </Typography>
                </Stack>
              </SectionCard>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
